import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';

/**
 * Hook to fetch products and prices from Firestore.
 * Returns { products, loading, error, retry }.
 *
 * Must match firebase/functions/access_control.py PLAN_LIMITS structure.
 */
export function useProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const productsRef = collection(db, 'products');
      const productsSnapshot = await getDocs(productsRef);

      const productsWithPrices = await Promise.all(
        productsSnapshot.docs.map(async (doc) => {
          const product = {
            id: doc.id,
            ...doc.data(),
            metadata: doc.data().metadata || {},
          };

          const pricesRef = collection(doc.ref, 'prices');
          const pricesSnapshot = await getDocs(pricesRef);

          const prices = pricesSnapshot.docs.map((priceDoc) => ({
            id: priceDoc.id,
            ...priceDoc.data(),
          }));

          return { ...product, prices };
        })
      );

      productsWithPrices.sort(
        (a, b) =>
          parseInt(a.metadata?.order || '0') - parseInt(b.metadata?.order || '0')
      );

      setProducts(productsWithPrices);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return { products, loading, error, retry: fetchProducts };
}
